import { type ReactNode } from "react";
import type {
  DashboardFilterConfig,
  FilterAccessor,
  FilterPredicates,
} from "./dashboardData";
import type { DashboardSort, DashboardView } from "./queryState";
import { useDashboardData } from "./useDashboardData";

export interface DashboardSortOption {
  id: string;
  dir: DashboardSort["dir"];
  label: string;
}

/**
 * Shared props + plumbing for the non-tabular collection components
 * (`DashboardList`, `DashboardGrid`). Lives in its own module so the component
 * files only export components (react-refresh rule).
 */
export interface DashboardCollectionProps<T> {
  items: T[];
  renderItem(item: T, index: number): ReactNode;
  getItemKey(item: T, index: number): string;
  searchable?: boolean;
  searchKeys?: (keyof T)[];
  searchPlaceholder?: string;
  /** Slot to the left of the search input (e.g. a select-all checkbox). */
  searchLeading?: ReactNode;
  /** Slot on the right of the search row (e.g. an export button). */
  searchTrailing?: ReactNode;
  filters?: DashboardFilterConfig[];
  /** Custom filter UI that replaces the generated `DashboardFilters` selects. */
  filtersSlot?: ReactNode;
  filterAccessor?: FilterAccessor<T>;
  filterPredicates?: FilterPredicates<T>;
  sortAccessor?: (item: T, id: string) => string | number;
  sortOptions?: DashboardSortOption[];
  toolbarActions?: ReactNode;
  createButton?: ReactNode;
  onRefresh?(): void;
  isLoading?: boolean;
  error?: string;
  empty?: {
    title: string;
    description?: string;
    action?: ReactNode;
    secondaryAction?: ReactNode;
  };
  viewSwitcher?: { value: DashboardView; options?: DashboardView[] };
  pageSizeOptions?: number[];
  /** Distinguish multiple collections on one page (separate URL params). */
  namespace?: string;
  /** Panel surface tone. Defaults to the canonical card surface. */
  surface?: "card" | "plain";
  className?: string;
}

export function useCollectionPlumbing<T>(props: DashboardCollectionProps<T>) {
  const { pageItems, total, query } = useDashboardData({
    items: props.items,
    searchKeys: props.searchKeys,
    filterAccessor: props.filterAccessor,
    filterPredicates: props.filterPredicates,
    sortAccessor: props.sortAccessor,
    namespace: props.namespace,
  });
  const { state } = query;
  const isFiltering =
    state.search.trim().length > 0 ||
    Object.values(state.filters).some((v) => v.length > 0);
  return { pageItems, total, query, state, isFiltering };
}
